import React, { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import usePlatformAdmin from 'dashboard/pages/platform/Admin/usePlatformAdmin';
import AdminSidebar from './Sidebar';
import AdminTopbar from './Topbar';
import OverviewTab from 'dashboard/pages/platform/Admin/OverviewTab';
import UsersTab from 'dashboard/pages/platform/Admin/UsersTab';
import BrandsTab from 'dashboard/pages/platform/Admin/BrandsTab';
import DomainsTab from 'dashboard/pages/platform/Admin/DomainsTab';
import SubscriptionsTab from 'dashboard/pages/platform/Admin/SubscriptionsTab';
import PlansTab from 'dashboard/pages/platform/Admin/PlansTab';
import EarningsTab from 'dashboard/pages/platform/Admin/EarningsTab';
import ErrorsTab from 'dashboard/pages/platform/Admin/ErrorsTab';
import AuditTab from 'dashboard/pages/platform/Admin/AuditTab';

/**
 * Top-level layout for the Platform Admin panel.
 *
 * This is intentionally NOT the brand dashboard layout. Brand owners
 * never reach this tree — the brand sidebar has no link to it, and the
 * layout itself gates on `usePlatformAdmin()`. Every server endpoint
 * the admin pages call is independently gated by `requirePlatformAdmin`
 * on the backend, so a leaked URL still 403s.
 *
 * Mounted at `/admin/*` (see pages/routes.jsx).
 */
export default function PlatformAdminLayout() {
  const { token, user } = useAuth();
  const isPlatformAdmin = usePlatformAdmin();
  const navigate = useNavigate();

  // Intentionally NOT refreshing the user profile here.
  // The verify endpoint already stamps `is_platform_admin` on the user
  // object that `login()` cached. If we re-fetched via findByUserId on
  // every mount we'd clobber that flag whenever findByUserId's response
  // shape changes / hasn't redeployed yet — which is exactly the bug
  // that kept routing the admin back to the brand dashboard.
  // Trust the cached user; the backend independently gates every
  // /admin/* API call via requirePlatformAdmin.

  // No token → kick to the landing page. Sign-in is the AuthModal on
  // the landing; there's no dedicated login route anymore.
  useEffect(() => {
    if (!token) navigate('/', { replace: true });
  }, [token, navigate]);

  // Token present but user hasn't loaded yet — wait one frame.
  if (token && !user) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // User loaded but not the admin — bounce to brand overview. The
  // backend also rejects every /admin/* API call with 403; this just
  // prevents the empty chrome from rendering.
  if (token && user && !isPlatformAdmin) {
    return <Navigate to='/dashboard/brand/overview' replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AdminSidebar />
      <Box sx={{ ml: '240px', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AdminTopbar />
        <Box sx={{ flex: 1, p: { xs: '20px', md: '28px' } }}>
          <Routes>
            <Route path='/' element={<Navigate to='/admin/overview' replace />} />
            <Route path='overview'      element={<OverviewTab />} />
            <Route path='users'         element={<UsersTab />} />
            <Route path='brands'        element={<BrandsTab />} />
            <Route path='domains'       element={<DomainsTab />} />
            <Route path='plans' element={<PlansTab />} />
            <Route path='subscriptions' element={<SubscriptionsTab />} />
            <Route path='earnings'      element={<EarningsTab />} />
            <Route path='errors'        element={<ErrorsTab />} />
            <Route path='audit'         element={<AuditTab />} />
            <Route path='*'             element={<Navigate to='/admin/overview' replace />} />
          </Routes>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
